import { useImperativeHandle } from 'react';
import * as React from 'react';
import { useTranslation } from 'utils/translation';
import SelectAdapter from './Adapter/SelectAdapter';
import DateAdapter from './Adapter/DateAdapter';
import DateRangeAdapter from './Adapter/DateRangeAdapter';
import Button from '../Button/Button';
import OptionsButton from '../Button/OptionsButton';
import Icon from '../Icon/Icon';
import css from './filters.module.scss';
import clsx from 'clsx';
import { AdapterProps, SelectAdapterProps, SearchAdapterProps } from './types';
import { BaseValues, useFiltersContext } from './FiltersContext';
import SearchAdapter from './Adapter/SearchAdapter';
import { AnimatePresence, motion } from 'motion/react';

type AbstractFilter = {
  name: string;
  label?: string;
};

type SearchFilter = AbstractFilter &
  SearchAdapterProps & {
    type: 'query';
    settings?: any;
  };

type SelectFilter = AbstractFilter &
  SelectAdapterProps & {
    type: 'select';
  };

type DateRangeFilter = AbstractFilter & {
  type: 'date-range';
  defaultValue?: number[];
};

type DateFilter = AbstractFilter & {
  type: 'date';
  defaultValue?: number;
};

export type Filter = SelectFilter | DateRangeFilter | DateFilter | SearchFilter;
export type FilterType = 'select' | 'date' | 'date-range' | 'query';

type ComponentMap = {
  select: React.FunctionComponent<
    Omit<SelectFilter, 'type'> & SelectAdapterProps
  >;
  date: React.FunctionComponent<Omit<DateFilter, 'type'>>;
  query: React.FunctionComponent<Omit<SearchFilter, 'type'>>;
  'date-range': React.FunctionComponent<Omit<DateRangeFilter, 'type'>>;
};

const map: {
  [Key in FilterType]: ComponentMap[Key];
} = {
  // @ts-ignore
  select: SelectAdapter,
  date: DateAdapter,
  'date-range': DateRangeAdapter,
  query: SearchAdapter,
};

export const SingleFilter: React.FC<Filter & AdapterProps> = ({
  type,
  ...rest
}) => {
  const Component = map[type] as any;

  return <Component {...rest} />;
};

export type Controller = {
  getActiveFilters(): Filter[];
  getAvailableFilters(): Filter[];
};

type Props<Values extends BaseValues = {}> = {
  items: Filter[];
  isLoading?: boolean;
  totalCount?: number;
  onSubmit?(values: Values): void;
  controllerRef?: React.MutableRefObject<Controller | undefined>;
};

const Filters: React.FC<Props> = ({
  items,
  isLoading = false,
  totalCount,
  onSubmit,
  controllerRef,
}) => {
  const { t } = useTranslation();
  const filters = useFiltersContext();
  const possibleItems = items.filter((i) => !!map[i.type]);
  const available = possibleItems.filter(
    (i) => !filters.enabled.includes(i.name),
  );
  const active = filters.enabled
    .map((name) => possibleItems.find((i) => i.name === name)!)
    .filter(Boolean);

  useImperativeHandle(controllerRef, () => ({
    getActiveFilters: () => active,
    getAvailableFilters: () => available,
  }));

  return (
    <div className={css.filters}>
      <div className={css.list}>
        <AnimatePresence>
          {active.map((item) => (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              transition={{ duration: 0.15 }}
              key={item.name}
              className={clsx(css.item, css[item.type])}
            >
              <SingleFilter
                value={filters.values[item.name]}
                onChange={(value) => filters.setValue(item.name, value)}
                onRemove={() => {
                  filters.remove(item.name);
                  filters.setEnabled((prev) =>
                    prev.filter((i) => i !== item.name),
                  );
                }}
                {...item}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {available.length > 0 && (
          <div className={css['add-filter']}>
            <OptionsButton
              startIcon={<Icon name={'far fa-circle-plus'} />}
              variant={'text'}
              actions={available.map((filter) => ({
                label: filter.label ?? filter.name,
                action() {
                  filters.setEnabled((prev) => [...prev, filter.name]);
                },
              }))}
            >
              {t('filter.add_filter')}
            </OptionsButton>
          </div>
        )}
      </div>
      <div className={css.buttons}>
        {onSubmit && (
          <Button
            onClick={() => onSubmit && onSubmit(filters.values)}
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <Icon name="fa-duotone fa-spinner fa-spin" />
              ) : Number(totalCount) > 0 ? (
                `(${totalCount})`
              ) : null
            }
          >
            {t('filter.show_result')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Filters;
